import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import { compareAsc, startOfDay } from 'date-fns';
import { MatchesListPage } from '@ui/fixtures/components/MatchesListPage/MatchesListPage';
import { useIsMobile } from '../../utils/useIsMobile';
import { getCurrentMonth, getMonth, getPreviousMonth, getUTCDate, MonthData } from '@ui/utils/date';
import { MatchPeriodFilter, MatchSort, MatchStatusFilter, MatchStatus } from '@repo/types';
import { useMatchFiltersQuery } from '@data/matches/useMatchFiltersQuery';
import { useMatchesQuery } from '@data/matches/useMatchesQuery';
import { useClubsQuery } from '@data/clubs/useClubsQuery';
import { Helmet } from 'react-helmet-async';
import { NotFoundPage } from '../NotFoundPage';
import { Loader } from '@ui/common/components/Loader/Loader';
import { useTenantFeatureFlagsQuery } from '@data/tenants/useTenantFeatureFlagsQuery';

const getStartDate = (startDate: string | null) => {
  if (!startDate) {
    return getCurrentMonth();
  } else {
    const jsDate = new Date(startDate);
    const d = getUTCDate(jsDate.getFullYear(), jsDate.getMonth(), jsDate.getDate());

    return {
      ...getMonth(d),
      date: d,
    };
  }
};

export function ResultsPage() {
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const startDateSearchParam = searchParams.get('startDate');
  const clubSearchParam = searchParams.get('club');
  const competitionSearchParam = searchParams.get('competition');
  const [updatedMonthToShow, setUpdatedMonthToShow] = useState<MonthData | null>(null);
  const currentMonth = useMemo(() => getStartDate(startDateSearchParam), [startDateSearchParam]);
  const [queryStartDate, setQueryStartDate] = useState(currentMonth.startDate);
  const [queryEndDate, setQueryEndDate] = useState(currentMonth.endDate);

  const { data: clubs, isLoading: areClubsLoading } = useClubsQuery();
  const { data: matchFilters, isLoading: matchFiltersLoading } = useMatchFiltersQuery();
  const { data: tenantFeatureFlags, isLoading: isLoadingFeatureFlags } = useTenantFeatureFlagsQuery();

  const selectedCompetition = useMemo(() => {
    return matchFilters?.find((matchFilter) => matchFilter.competitions === competitionSearchParam);
  }, [matchFilters, competitionSearchParam]);

  const {
    data: matches,
    isLoading: matchesLoading,
    isFetchedAfterMount: areMatchesFetchedAfterMount,
    error: matchesError,
  } = useMatchesQuery(
    {
      limit: 300,
      period: MatchPeriodFilter.Custom,
      startDate: queryStartDate,
      endDate: queryEndDate,
      sort: MatchSort.DESC,
      status: MatchStatusFilter.RESULT,
      teamId: clubSearchParam,
      competition: selectedCompetition?.competitions ?? matchFilters?.[0]?.competitions,
    },
    {
      enabled: !!matchFilters,
    }
  );

  const matchesWithoutPostponed = useMemo(() => {
    const today = startOfDay(new Date());

    return matches?.items.filter((match) => {
      if (match.status !== MatchStatus.Postponed && match.status !== MatchStatus.Abandoned) {
        return true;
      }

      const matchDate = new Date(match.date);

      if (compareAsc(today, matchDate) <= 0) {
        return true;
      }
      return false;
    });
  }, [matches]);

  useEffect(() => {
    setQueryStartDate(currentMonth.startDate);
    setQueryEndDate(currentMonth.endDate);
  }, [currentMonth]);

  useEffect(() => {
    if (
      !matchesLoading &&
      areMatchesFetchedAfterMount &&
      !startDateSearchParam &&
      (!matchesWithoutPostponed || matchesWithoutPostponed?.length === 0)
    ) {
      const prevMonth = getPreviousMonth(getCurrentMonth().date);
      setQueryStartDate(prevMonth.startDate);
      setQueryEndDate(prevMonth.endDate);
      setUpdatedMonthToShow(prevMonth);
    }
  }, [areMatchesFetchedAfterMount, matchesLoading, matchesWithoutPostponed?.length, currentMonth]);

  if (matchFiltersLoading || matchesLoading || areClubsLoading || isLoadingFeatureFlags) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
        }}
      >
        <Loader />
      </div>
    );
  }

  if (matchesError || !matchFilters || !matchesWithoutPostponed || !clubs || !tenantFeatureFlags) {
    return <NotFoundPage />;
  }

  const metaTitle = `${import.meta.env.VITE_SITE_NAME} Football Scores, Results & Season Archives`;
  const metaDescription = `View ${
    import.meta.env.VITE_SITE_NAME
  } scores, results & season archives, along with other competitions involving ${
    import.meta.env.VITE_SITE_NAME
  } clubs, on the official website of the ${import.meta.env.VITE_SITE_NAME}.`;

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
      </Helmet>
      <MatchesListPage
        title={'Results'}
        isMobile={isMobile}
        matchFilters={matchFilters}
        selectedCompetition={selectedCompetition ?? matchFilters[0]}
        matches={matchesWithoutPostponed}
        clubs={clubs.items}
        currentMonth={updatedMonthToShow ?? currentMonth}
        monthSwitcherMode="RESULTS"
        featureFlags={tenantFeatureFlags}
        sortByDate={'DESC'}
      />
    </>
  );
}
